/**
 * Host-agnostic execution machinery for parent tool calls: double-mount
 * deduplication, serialized Office.run() execution with per-call timeouts, and
 * Office.js error → actionable-message mapping.
 *
 * Every Office add-in dispatches a turn's tool calls into its own
 * `Word.run()` / `Excel.run()` / `PowerPoint.run()`. This module wraps that
 * boundary so the same protections apply to every host — the `.run()` itself
 * lives inside the `handler` each host passes in, so nothing here depends on a
 * host global. First proven in PowerPoint (fragment 06e3f48d), then promoted
 * here so Word/Excel stop timing out under parallel dispatch.
 */

// WHY BOTH DEDUP AND SERIALIZATION EXIST
// ─────────────────────────────────────────────────────────────────────────────
//
// Problem 1 — Double-mount deduplication (inFlightCalls / completedResults)
// ─────────────────────────────────────────────────────────────────────────────
// In this environment (Office.js task pane on macOS / WKWebView + React legacy
// mode), two `window.addEventListener("message", ...)` listeners can become
// active simultaneously for a single UsableChatEmbed instance. This is caused
// by a combination of:
//
//   1. React legacy mode (ReactDOM.render) does not batch async state updates.
//      The two setState calls in useAuth's refreshAccessToken fire as separate
//      renders, which can briefly double-mount the chat pane and leave an
//      orphaned listener from the first mount.
//
//   2. Office.js / WKWebView has known quirks where the task pane JS context
//      can initialise in ways that React's effect cleanup does not fully
//      intercept.
//
// The result: a single TOOL_CALL postMessage is received by both listeners,
// causing the tool handler — and therefore Office.run() — to execute twice.
// Non-idempotent operations (add_slide, add_chart) produce duplicate artefacts;
// others silently double-write or fail on the second attempt.
//
// FIX: dedup by the embed's TOOL_CALL requestId. Re-delivery of ONE postMessage
// (the double-mount case — two listeners seeing the SAME message) carries the
// SAME requestId, so it collapses onto one handler execution; distinct AI calls
// always carry unique requestIds, so they all run.
//
// DEDUP KEY = requestId. History: dedup was originally content-keyed (toolName +
// JSON args) after an observation that duplicate deliveries carried different
// requestIds. Live deck-build testing (2026-07-02) proved content keying fatally
// wrong: the embed dispatches ALL of a turn's tool calls simultaneously, so a
// legitimate add_slide × 5 (args always `{}`) arrived within milliseconds →
// collapsed into ONE created slide while the model received five identical
// successes — its picture diverged and every later index was wrong. requestId
// separates the two exactly. If an iframe-side bug ever re-issues a call under a
// new requestId, the worst case is a duplicated (visible, cosmetic) operation —
// strictly better than silently DROPPING operations the model asked for.
const inFlightCalls = new Map<string, Promise<unknown>>();
const completedResults = new Map<string, { result: unknown; ts: number }>();
/** Re-delivery can trail completion; requestIds are unique so a long TTL is safe. */
const COMPLETED_TTL_MS = 5_000;

// Problem 2 — Concurrent Office.run() calls (executionChain)
// ─────────────────────────────────────────────────────────────────────────────
// When the AI sends many tool calls in rapid succession (e.g. 14 set_table_data
// calls, or several formatting tools in one turn), the embed SDK dispatches them
// all immediately. Each dispatch triggers a separate Office.run() context.
// Office.js cannot handle many concurrent contexts — some never resolve their
// promise, producing a `pending_parent_response` (tool timeout) on the AI side.
//
// FIX: serialize all Office.run() calls through a single promise chain. Each
// call waits for the previous one to settle before starting. Errors on one call
// are swallowed from the chain so they don't block subsequent calls.
let executionChain: Promise<unknown> = Promise.resolve();

/** Default per-call timeout. Screenshot/compose-style tools get the long one. */
export const DEFAULT_TOOL_TIMEOUT_MS = 15_000;
export const LONG_TOOL_TIMEOUT_MS = 30_000;

export interface SerializeOptions {
  timeoutMs?: number;
  /** Name used in the timeout message (usually the tool name). */
  label?: string;
}

/**
 * Run `fn` on the serialized execution chain, racing it against a timeout
 * INSIDE the chain link. The placement matters: racing the *outer* promise
 * would reject the caller but leave the chain itself awaiting the hung
 * `context.sync()` forever — wedging every subsequent call. Racing inside the
 * link means the chain advances past a timed-out call.
 *
 * Office.js has no cancellation: a timed-out operation may still complete in
 * the background, and if the host is truly wedged later calls may time out
 * too. This is best-effort unwedging, and the error message says so.
 */
export function serializeExecution<T>(
  fn: () => Promise<T>,
  options: SerializeOptions = {}
): Promise<T> {
  const { timeoutMs = DEFAULT_TOOL_TIMEOUT_MS, label = "tool call" } = options;

  const runWithTimeout = (): Promise<T> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        reject(
          new Error(
            `${label} timed out after ${Math.round(timeoutMs / 1000)}s — the host did not respond. ` +
              `It may be showing a dialog or be busy; the operation may still complete in the background. ` +
              `Re-read the current state before retrying.`
          )
        );
      }, timeoutMs);
    });
    return Promise.race([fn(), timeout]).finally(() => {
      if (timer !== undefined) clearTimeout(timer);
    }) as Promise<T>;
  };

  const result = executionChain.then(runWithTimeout) as Promise<T>;
  executionChain = result.then(
    () => undefined,
    () => undefined // don't let a failed/timed-out call block the queue
  );
  return result;
}

// ---------------------------------------------------------------------------
// Office.js error mapping
// ---------------------------------------------------------------------------

/** Hints for the Office.js error codes the AI can actually act on. */
const OFFICE_ERROR_HINTS: Record<string, string> = {
  InvalidArgument:
    "an index or id is likely out of range — re-read the current state (a get/list tool) first",
  ItemNotFound: "the item no longer exists — re-read the current state (a get/list tool)",
  GeneralException:
    "this API may require a newer Office version than this host provides, or the app is in a transient state — retry once, then report the limitation to the user",
  AccessDenied: "the host blocked the operation — the document may be read-only",
};

/**
 * Turn an OfficeExtension.Error (or anything else) into an Error whose message
 * carries the code, location, and an actionable hint. Applied once at the
 * executeToolCall boundary so every tool benefits.
 */
export function describeOfficeError(err: unknown, toolName: string): Error {
  const officeErr = err as {
    code?: string;
    message?: string;
    debugInfo?: { errorLocation?: string };
  } | null;

  if (officeErr && typeof officeErr === "object" && typeof officeErr.code === "string") {
    const hint = OFFICE_ERROR_HINTS[officeErr.code];
    const location = officeErr.debugInfo?.errorLocation
      ? ` at ${officeErr.debugInfo.errorLocation}`
      : "";
    return new Error(
      `${toolName} failed: ${officeErr.code}${location} — ${officeErr.message ?? "no message"}` +
        (hint ? ` (hint: ${hint})` : "")
    );
  }
  return err instanceof Error ? err : new Error(String(err));
}

// ---------------------------------------------------------------------------
// The tool-call boundary: dedup + serialize + timeout + error mapping
// ---------------------------------------------------------------------------

export interface ExecuteOptions {
  timeoutMs?: number;
  /**
   * The embed's TOOL_CALL requestId — the exact dedup identity (see WHY
   * above). When absent (defensive), falls back to a content key, which can
   * over-dedup argument-less tools; always pass it when available.
   */
  requestId?: string;
}

/**
 * Execute one tool call with the full protection stack. `handler` should wrap
 * exactly one Office.run() (Word/Excel/PowerPoint).
 */
export async function executeToolCall(
  toolName: string,
  args: unknown,
  handler: () => Promise<unknown>,
  options: ExecuteOptions = {}
): Promise<unknown> {
  const key =
    options.requestId && options.requestId.length > 0
      ? `req:${options.requestId}`
      : `${toolName}:${JSON.stringify(args)}`;

  // Re-delivery of the same TOOL_CALL while the first is still running.
  const existing = inFlightCalls.get(key);
  if (existing) return existing;

  // Re-delivery trailing completion — return the same result object (the
  // image-attachment WeakSet relies on object identity to deliver once).
  const cached = completedResults.get(key);
  if (cached && Date.now() - cached.ts < COMPLETED_TTL_MS) {
    return Promise.resolve(cached.result);
  }

  const promise = serializeExecution(handler, {
    timeoutMs: options.timeoutMs,
    label: toolName,
  }).then(
    (result) => {
      inFlightCalls.delete(key);
      completedResults.set(key, { result, ts: Date.now() });
      // Bound the completed map — requestIds are unique, so entries never
      // get overwritten; sweep expired ones opportunistically.
      if (completedResults.size > 64) {
        const cutoff = Date.now() - COMPLETED_TTL_MS;
        completedResults.forEach(({ ts }, k) => {
          if (ts < cutoff) completedResults.delete(k);
        });
      }
      return result;
    },
    (err) => {
      inFlightCalls.delete(key);
      throw describeOfficeError(err, toolName);
    }
  );
  inFlightCalls.set(key, promise);
  return promise;
}

/** Test-only: clear dedup + chain state between cases. */
export function __resetToolExecutionForTests(): void {
  inFlightCalls.clear();
  completedResults.clear();
  executionChain = Promise.resolve();
}
